/**
 * The purpose of this file is to combine reducers and create the single store.
 * One should use it simply like so:
 *
 * ```import STORE, { persistor } from "./redux"```
 */
import { legacy_createStore as createStore, applyMiddleware, combineReducers } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import thunk from "redux-thunk";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // uses localStorage for web

import selection from "./selection";
import query from "./query";
import settings from "./settings";
import urlParams from "./urlParams";

const middleWares = [thunk];

// Add logger middleware only in development
if (import.meta.env.NODE_ENV === "development" && console && console.groupCollapsed) {
    const logger = (store) => (next) => (action) => {
        let result;
        if (!action.__no_log) {
            console.groupCollapsed(action.type);
            console.info("dispatching", action);
            result = next(action);
            console.log("next state", store.getState());
            console.groupEnd(action.type);
        } else {
            result = next(action);
        }
        return result;
    };
    middleWares.push(logger);
}

// 🔹 Combine all reducers
const appReducer = combineReducers({
    selection,
    query,
    settings,
    urlParams,
});

const rootReducer = (state, action) => {
    if (action.type === "LOGOUT") {
        // Clear all persisted data
        storage.removeItem("persist:root");
        // Reset redux state
        state = undefined;
    }
    return appReducer(state, action);
};


// 🔹 Redux Persist configuration
const persistConfig = {
    // Key bumped from "root" to "root-v2" on purpose. An earlier build persisted
    // the whole state (including settings.server), so browsers that used it still
    // have a "persist:root" blob in localStorage holding a STALE settings.server
    // pointing at the raw ALB DNS. redux-persist rehydrates that over the fresh,
    // env-derived config, so the app calls the ALB directly and the browser throws
    // ERR_CERT_COMMON_NAME_INVALID (the ALB cert is for insights.clinxos.com, not
    // the ALB DNS). Changing the key makes redux-persist ignore the old blob and
    // start clean, so every browser self-heals on next load without manual cache
    // clearing. Only the `query` slice is persisted now; settings always comes
    // from config.default.js (VITE_BASE_URL).
    key: "root-v2",
    storage,
    whitelist: ["query"], // persist only the `query` slice
};

// 🔹 Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// 🔹 Create store
const store = createStore(
    persistedReducer,
    composeWithDevTools(applyMiddleware(...middleWares))
);

// 🔹 Create persistor
const persistor = persistStore(store);

export { store as default, persistor };
